'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUtmStorage } from '@/features/marketing/hooks/use-utm-storage';
import { setPrimaryToken } from '@/lib/auth/storage';
import { shouldEnableMarketingTags } from '@/lib/tracking/marketing-tags';

import {
  getPasswordStrength,
  getTabCopy,
  signInWithCredentials,
  signInWithGoogle,
  signUpWithCredentials,
  validateEmail,
} from './login-auth';
import type { AuthFlowType } from './login-redirect';
import { readPostAuthRedirectParam } from './login-redirect';
import { hasExistingSession } from './login-storage';
import { HIRED_BY_COMPANIES } from './login-testimonials';
import { LoginTestimonialCarousel } from './LoginTestimonialCarousel';

type GoogleAccounts = {
  id: {
    initialize: (config: Record<string, unknown>) => void;
    renderButton: (el: HTMLElement, config: Record<string, unknown>) => void;
  };
};

function recordRegistration(): void {
  if (!shouldEnableMarketingTags()) {
    return;
  }
  try {
    const fbq = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof fbq === 'function') {
      fbq('track', 'CompleteRegistration');
    }
  } catch {
    // legacy parity: swallow pixel errors
  }
}

export function LoginPageClient({
  webDomain,
  googleClientId,
}: {
  webDomain: string;
  googleClientId: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const signupGoogleRef = useRef<HTMLDivElement>(null);
  const signinGoogleRef = useRef<HTMLDivElement>(null);
  const googleInitialized = useRef(false);

  const initialTab = searchParams.get('tab') === 'signup' ? 'signup' : 'signin';
  const preferredRedirect = readPostAuthRedirectParam(searchParams);
  const [activeTab, setActiveTab] = useState<AuthFlowType>(initialTab);
  const [signinEmail, setSigninEmail] = useState('');
  const [signinPassword, setSigninPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<{ label: string; color: string } | null>(null);
  const [signinLoading, setSigninLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  useUtmStorage();

  const redirectTo = useCallback(
    (path: string) => {
      if (path.startsWith('http')) {
        window.location.href = path;
        return;
      }
      router.replace(path);
    },
    [router],
  );

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      const result = await signInWithGoogle(credential, preferredRedirect);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      recordRegistration();
      redirectTo(result.redirectTo);
    },
    [redirectTo, preferredRedirect],
  );

  const initGoogleButtons = useCallback(() => {
    if (googleInitialized.current) {
      return;
    }
    const googleApi = (window as Window & { google?: { accounts: GoogleAccounts } }).google;
    if (!googleApi?.accounts?.id) {
      return;
    }

    googleApi.accounts.id.initialize({
      client_id: googleClientId,
      ux_mode: 'popup',
      cancel_on_tap_outside: true,
      itp_support: true,
      callback: (response: { credential: string }) => {
        void handleGoogleCredential(response.credential);
      },
    });

    const render = (el: HTMLDivElement | null) => {
      if (!el) {
        return;
      }
      el.innerHTML = '';
      googleApi.accounts.id.renderButton(el, {
        type: 'standard',
        shape: 'rectangular',
        text: 'signin_with',
        size: 'large',
        width: Math.max(el.clientWidth || 290, 290),
        logo_alignment: 'center',
      });
    };

    render(signupGoogleRef.current);
    render(signinGoogleRef.current);
    googleInitialized.current = true;
  }, [googleClientId, handleGoogleCredential]);

  useEffect(() => {
    if (hasExistingSession()) {
      redirectTo(preferredRedirect || '/account/dashboard');
      return;
    }

    const authToken = searchParams.get('auth_token');
    const redirectPath = readPostAuthRedirectParam(searchParams);
    if (authToken && redirectPath) {
      setPrimaryToken(authToken);
      redirectTo(redirectPath || '/account/portfoliosettings');
      return;
    }

    const provider = searchParams.get('provider');
    const providerToken = searchParams.get('token');
    if (provider && providerToken) {
      setPrimaryToken(providerToken);
      const signinOrSignup = searchParams.get('signin_or_signup');
      if (signinOrSignup === 'sign-in') {
        redirectTo(preferredRedirect || '/account/dashboard');
      } else {
        redirectTo('/account/portfoliosettings?mode=editor&onBoard=true');
      }
      return;
    }

    const autologin = searchParams.get('autologin');
    const useremail = searchParams.get('useremail');
    const encodedPassword = searchParams.get('token');
    if (autologin && useremail && encodedPassword) {
      const password = atob(encodedPassword);
      void signInWithCredentials(useremail, password, preferredRedirect).then((result) => {
        if (result.ok) {
          redirectTo(result.redirectTo);
        } else {
          toast.error(result.message);
        }
      });
    }
  }, [redirectTo, searchParams, preferredRedirect]);

  useEffect(() => {
    if (searchParams.get('message') !== 'passwordresetsuccess') {
      return;
    }
    toast.success('Reset password successful. You may now login to the site.');
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      initGoogleButtons();
    }, 250);
    return () => window.clearInterval(timer);
  }, [initGoogleButtons]);

  const tabCopy = getTabCopy(activeTab);

  const handleSigninSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSigninLoading(true);
    try {
      const result = await signInWithCredentials(
        signinEmail,
        signinPassword,
        preferredRedirect,
      );
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      redirectTo(result.redirectTo);
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSigninLoading(false);
    }
  };

  const handleSignupSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateEmail(signupEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSignupLoading(true);
    try {
      const result = await signUpWithCredentials(
        signupName,
        signupEmail,
        signupPassword,
        preferredRedirect,
      );
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      recordRegistration();
      redirectTo(result.redirectTo);
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const switchTab = (tab: AuthFlowType) => {
    setActiveTab(tab);
  };

  return (
    <div className="container">
      <Link href={webDomain} className="kw-logo-right">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="top-logo-mobile"
          alt="KudosWall Pro Logo"
          src="/login-assets/images/pro-logo-teal.png"
          height={63}
        />
      </Link>

      <div className="left-section col-md-7">
        <div>
          <Link href={webDomain} className="kw-logo">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="top-logo"
              alt="KudosWall Pro Logo"
              src="/login-assets/images/pro-logo-teal.png"
              height={63}
            />
          </Link>
          <div className="contents">
            <h5>KudosWall&apos;s AI-Driven Career Tools—Trusted by 350,000+ Job Seekers Worldwide</h5>
            <p>
              Create, refine, and optimize your resume to make a lasting impression on recruiters with
              KudosWall&apos;s AI tools. Save time and increase your interview chances with these advanced
              features.
            </p>
          </div>
        </div>

        <LoginTestimonialCarousel />

        <div className="features-sections">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/login-assets/images/pana.svg"
            alt=""
            className="features-imgs"
            width={320}
            height={120}
            loading="eager"
            decoding="async"
          />
        </div>
      </div>

      <div className="right-section col-md-7">
        <div className="writing_pro_resumes_sign new_pro_sign_in" id="authenticate-msg-m">
          <div className="login_pro_section">
            <h1 id="page-title">{tabCopy.title}</h1>
            <h3 id="page-sub-title">{tabCopy.subtitle}</h3>
          </div>

          <ul className="nav nav-pills">
            <li>
              <a
                id="signin-header"
                className={`tab-link${activeTab === 'signin' ? ' active' : ''}`}
                href="#signin"
                onClick={(event) => {
                  event.preventDefault();
                  switchTab('signin');
                }}
              >
                Sign In
              </a>
            </li>
            <li>
              <a
                id="signup-header"
                className={`tab-link${activeTab === 'signup' ? ' active' : ''}`}
                href="#signup"
                onClick={(event) => {
                  event.preventDefault();
                  switchTab('signup');
                }}
              >
                Sign Up
              </a>
            </li>
          </ul>

          <div className="tab-content">
            <div id="signup" className={`tab-pane form${activeTab === 'signup' ? ' active' : ''}`}>
              <form id="signup-form" onSubmit={handleSignupSubmit}>
                <ul>
                  <li>
                    <div className="social_network social-login clearfix">
                      <ul>
                        <li>
                          <div className="google-btn-container custom-google-btn">
                            <div id="google-signin">
                              <div ref={signupGoogleRef} className="g_id_signin" style={{ width: '100%' }} />
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <li>
                    <p className="or_class_in clearfix">
                      <span className="before_or"> </span>
                      <span className="in_class_sign">or</span>
                    </p>
                  </li>
                  <li>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your Full Name(Only for new users)"
                      required
                      value={signupName}
                      onChange={(event) => setSignupName(event.target.value)}
                    />
                  </li>
                  <li>
                    <input
                      type="email"
                      id="email"
                      name="username"
                      placeholder="Email@example.com"
                      required
                      value={signupEmail}
                      onChange={(event) => setSignupEmail(event.target.value)}
                    />
                  </li>
                  <li>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Password"
                      required
                      value={signupPassword}
                      onChange={(event) => {
                        setSignupPassword(event.target.value);
                        setPasswordStrength(getPasswordStrength(event.target.value));
                      }}
                    />
                    <div
                      id="password-strength"
                      style={passwordStrength ? { color: passwordStrength.color } : undefined}
                    >
                      {passwordStrength?.label || ''}
                    </div>
                  </li>
                  <li>
                    <button
                      id="signup-button"
                      className={`sign_up_its_free${signupLoading ? ' button-loading' : ''}`}
                      type="submit"
                      disabled={signupLoading}
                    >
                      <span className="button-text" style={{ display: signupLoading ? 'none' : 'flex' }}>
                        Sign Up
                      </span>
                      <span className="spinner" style={{ display: signupLoading ? 'flex' : 'none' }} />
                    </button>
                  </li>
                  <li className="not-registered-text">
                    <span>
                      Already have an account?
                      <a
                        className="tab-link"
                        href="#signin"
                        onClick={(event) => {
                          event.preventDefault();
                          switchTab('signin');
                        }}
                      >
                        Login
                      </a>
                    </span>
                  </li>
                </ul>
              </form>
            </div>

            <div id="signin" className={`tab-pane form${activeTab === 'signin' ? ' active' : ''}`}>
              <form id="signin-form" onSubmit={handleSigninSubmit}>
                <ul>
                  <li>
                    <input
                      type="email"
                      id="signin-email"
                      name="username"
                      placeholder="Email@example.com"
                      required
                      value={signinEmail}
                      onChange={(event) => setSigninEmail(event.target.value)}
                    />
                  </li>
                  <li>
                    <input
                      type="password"
                      id="signin-password"
                      name="password"
                      placeholder="Password"
                      required
                      value={signinPassword}
                      onChange={(event) => setSigninPassword(event.target.value)}
                    />
                  </li>
                  <li>
                    <button
                      id="signin-button"
                      className={`sign_up_its_free${signinLoading ? ' button-loading' : ''}`}
                      type="submit"
                      disabled={signinLoading}
                    >
                      <span className="button-text" style={{ display: signinLoading ? 'none' : 'flex' }}>
                        Sign In
                      </span>
                      <span className="spinner" style={{ display: signinLoading ? 'flex' : 'none' }} />
                    </button>
                  </li>
                  <li>
                    <p className="forgot_password_section clearfix">
                      <Link href="/forgot" className="forgot_password_section_sign">
                        Forgot Password?
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p className="or_class_in clearfix">
                      <span className="before_or"> </span>
                      <span className="in_class_sign">or</span>
                    </p>
                  </li>
                  <li>
                    <div className="social_network social-login clearfix">
                      <ul>
                        <li>
                          <div className="google-btn-container custom-google-btn">
                            <div id="google-signin">
                              <div ref={signinGoogleRef} className="g_id_signin" style={{ width: '100%' }} />
                            </div>
                          </div>
                        </li>
                        <li className="not-registered-text">
                          <span>
                            Not registered yet?
                            <a
                              className="tab-link"
                              href="#signup"
                              onClick={(event) => {
                                event.preventDefault();
                                switchTab('signup');
                              }}
                            >
                              Create an account
                            </a>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </form>
            </div>
          </div>
        </div>

        <div className="logo-container">
          <span className="compaines">KudosWall users are hired by:</span>
          <div className="logo-track">
            {HIRED_BY_COMPANIES.map((company) => (
              <span key={company} className="client-logo">
                {company}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
